import { useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeCardElement } from '@stripe/stripe-js';
import { useMutation, useQueryClient } from 'react-query';
import { creditCardsApi, paymentIntentApi, creditCardsKeys } from '../api';
import { Stripe } from '@stripe/stripe-js';
import { BillingDetails } from '~/types/stripe';
import { useContext } from 'react';
import FlashContext from '~/App/Components/Flash/Context';

export interface CreateCreditCardProps {
  intent_id: string;
}

const useCreditCard = () => {
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();
  const { createFlash } = useContext(FlashContext);

  const confirmCreditCardAction = async (data: {
    billingDetails: BillingDetails;
    cardElement: StripeCardElement;
  }) => {
    const paymentIntent = await paymentIntentApi().get('');
    const secret = paymentIntent.client_secret;

    const payload = {
      payment_method: {
        card: data.cardElement,
        billing_details: data.billingDetails,
      },
    };
    return await (stripe as Stripe).confirmCardSetup(secret, payload);
  };

  const {
    mutateAsync: confirmCreditCardMutation,
    isLoading: isConfirmingCreditCard,
  } = useMutation(confirmCreditCardAction, {
    onSuccess: (result) => {
      if (result.error) {
        createFlash({
          id: 'update-credit-card',
          title: 'Error',
          message: 'Please check your input fields. ' + result.error.message,
          options: { style: 'danger' },
        });
      }
    },
  });

  const {
    mutateAsync: createCreditCardMutation,
    isLoading: isCreatingCreditCard,
  } = useMutation(
    (data: CreateCreditCardProps) =>
      creditCardsApi().post<CreateCreditCardProps>(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(creditCardsKeys.lists());
      },
    }
  );

  return {
    stripe,
    elements,
    confirmCreditCardMutation,
    isConfirmingCreditCard,
    createCreditCardMutation,
    isCreatingCreditCard,
  };
};

export default useCreditCard;
